import { UserType } from '@metadevprofile/shared';
import { buttonVariants } from '@metadevprofile/ui/web';
import { BellIcon, BookOpenIcon, CreditCardIcon, LayoutDashboardIcon, UserIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, NavLink, Outlet } from 'react-router';
import { selectUser } from '@/auth/stores/auth-slice';
import { CrispWeb } from '@/common/components/crisp-web';
import { Footer } from '@/common/components/sidebar/footer';
import { MainContentArea } from '@/common/components/sidebar/main-content-area';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarProvider,
} from '@/common/components/sidebar/sidebar';
import { ToggleSidebar } from '@/common/components/sidebar/toggle-sidebar';
import { useMobileDevice } from '@/common/hooks/use-mobile-device';
import { getRouteByUserType } from '@/common/lib/get-route-by-user-type';
import { navLinkClass } from '@/common/lib/nav-link-class';
import { useAppSelector } from '@/common/redux/store';
import { MobileRedirectModal } from '@/practitioner/components/mobile-redirect-modal';
import { practitionerRoutes } from '@/practitioner/constants/routes';

export function PractitionerLayout() {
  const { t } = useTranslation('practitioner', { keyPrefix: 'navigation' });
  const user = useAppSelector(selectUser);
  const isMobileDevice = useMobileDevice();

  if (!user) return null;

  if (user.type !== UserType.PRACTITIONER) {
    return <Navigate to={getRouteByUserType(user)} replace />;
  }

  if (isMobileDevice) {
    return <MobileRedirectModal open={true} />;
  }

  const isIndependentPractitioner = !user.institution_id;

  const sidebarItems = [
    {
      label: t('dashboard'),
      icon: LayoutDashboardIcon,
      path: practitionerRoutes.dashboard,
    },
    {
      label: t('home'),
      icon: BookOpenIcon,
      path: practitionerRoutes.home,
    },
    {
      label: t('patients'),
      icon: UserIcon,
      path: practitionerRoutes.patients,
    },
    ...(isIndependentPractitioner
      ? [
          {
            label: t('subscription'),
            icon: CreditCardIcon,
            path: practitionerRoutes.subscription,
          },
        ]
      : []),
  ];

  return (
    <SidebarProvider>
      <ToggleSidebar />
      <Sidebar className="bg-surface text-foreground p-2">
        <SidebarHeader>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <img
                src="/images/logo.png"
                alt={t('logoAlt')}
                style={{ width: 32, height: 32 }}
                className="size-8 rounded-md"
              />
              <div className="text font-semibold">{t('brandName')}</div>
            </div>
            <Link
              to={practitionerRoutes.notifications}
              className={buttonVariants({ variant: 'ghost', isIconOnly: true })}
            >
              <BellIcon className="size-4" />
            </Link>
          </div>
        </SidebarHeader>
        <SidebarContent className="flex-1 mt-2">
          <SidebarGroup className="px-0">
            <SidebarGroupLabel className="text-xs text-muted font-semibold">
              {t('general').toUpperCase()}
            </SidebarGroupLabel>
            <SidebarGroupContent className="px-0">
              <SidebarMenu className="gap-2 p-2">
                {sidebarItems.map((item) => (
                  <SidebarMenuItem key={item.path}>
                    <NavLink to={item.path} end className={({ isActive }) => navLinkClass(isActive)}>
                      <item.icon className="size-4" />
                      <span>{item.label}</span>
                    </NavLink>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <Footer />
        </SidebarFooter>
      </Sidebar>

      <MainContentArea>
        <Outlet />
        <CrispWeb />
      </MainContentArea>
    </SidebarProvider>
  );
}
